import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayStatements, queriesWorkLogs } from "@query";
import {
  useWSModalCustom,
  WSAlert,
  WSCard,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { selectorWorkLogLabel } from "../../../WorkLogs/selectors/selectorWorkLogLabel";
import { PAYROLL_CURRENCY } from "../../constants";
import { parsePayrollAmount } from "../../utils";

type Props = {
  payStatementId: string;
  paystubNumber: string;
  /** Work logs this statement paid — all of them are released by a cancel. */
  workLogs: WorkLog[];
} & WSModalCustomProps<boolean>;

const ModalCancelPayStatement: React.FC<Props> = props => {
  // Cancelling detaches the statement's work logs, so the work-log lists have to
  // refetch alongside the statement reads the registry already invalidates.
  const [cancelPayStatement, meta] =
    queriesPayStatements.cancelPayStatement.useMutation({
      dependencies: [queriesWorkLogs.listWorkLogs.queryKeyBase]
    });

  const handleCancel = async () => {
    await cancelPayStatement(
      { payStatementId: props.payStatementId },
      { onSuccess: () => props.onClose(true) }
    );
  };

  return (
    <WSModal
      actions={{
        buttons: [
          {
            label: "Keep pay statement",
            kind: "Secondary",
            onClick: () => props.onClose(false)
          },
          {
            label: "Cancel pay statement",
            destructive: true,
            loading: meta.isLoading,
            onClick: handleCancel
          }
        ]
      }}
      size="S"
      title="Cancel pay statement"
      onClose={() => props.onClose(false)}
    >
      <WSList gap="M">
        <WSText.ParagraphSm color="gray500">
          {`Pay statement #${props.paystubNumber} will be deleted. This can't be undone.`}
        </WSText.ParagraphSm>

        {props.workLogs.length > 0 && (
          <>
            <WSAlert
              icon="alert"
              theme="warning"
              title={`${props.workLogs.length} work log${
                props.workLogs.length === 1 ? "" : "s"
              } will be released`}
            >
              These work logs go back to awaiting approval and are no longer
              scheduled for payment. You'll need to approve each of them again
              before they can be paid.
            </WSAlert>

            <WSCard>
              <WSList gap="S">
                {props.workLogs.map(workLog => (
                  <WSText.ParagraphSm key={workLog.id} color="gray600">
                    {`${selectorWorkLogLabel(workLog)} · ${formatMoney(
                      parsePayrollAmount(workLog.totalAmount),
                      PAYROLL_CURRENCY
                    )}`}
                  </WSText.ParagraphSm>
                ))}
              </WSList>
            </WSCard>
          </>
        )}

        <ErrorAlert context="PayStatementCancel" error={meta.error} />
      </WSList>
    </WSModal>
  );
};

export const useModalCancelPayStatement = () =>
  useWSModalCustom(ModalCancelPayStatement);
